import React, { ForwardedRef, ReactNode } from 'react';
import clsx from 'clsx';
import { useRadioGroup, AriaRadioGroupProps } from '@react-aria/radio';
import { useRadioGroupState } from '@react-stately/radio';
import styles from '@danske/sapphire-css/components/fieldGroup/fieldGroup.module.css';

import { Field } from '../../Field';
import { Label } from '../../Label';
import { RadioGroupContext } from './context';
import { SapphireStyleProps, useThemeCheck } from '../../utils';
import {
  AriaValidationPropsKeys,
  BaseComponentKeys,
  FocusEventsKeys,
  GlobalDomAttributes,
  InputBaseKeys,
  ValidationIsRequiredKey,
  ValueBasePropsKeys,
} from '../../utils/types';

export interface RadioGroupProps
  extends Pick<
      AriaRadioGroupProps,
      | 'orientation'
      | 'label'
      | InputBaseKeys
      | FocusEventsKeys
      | ValueBasePropsKeys
      | 'name'
      | AriaValidationPropsKeys
      | ValidationIsRequiredKey
      | BaseComponentKeys
    >,
    GlobalDomAttributes,
    SapphireStyleProps {
  /**
   * A HelpButton to render next to the label
   */
  contextualHelp?: React.ReactNode;
  /**
   * The Radio(s) contained within the RadioGroup.
   */
  children: ReactNode;

  /**
   * If set, the radio group becomes invalid with this associated error message.
   */
  error?: ReactNode;

  /**
   * A note to show below the radio group.
   * If the radio group has an error message, this note will be replaced by that.
   * Use {@Link Field.Note} to render the note.
   */
  note?: ReactNode;

  /**
   * @default 'lg'
   */
  size?: 'lg' | 'md';

  /**
   * To visually indicate if this field is required or optional.
   * @default false
   */
  necessityIndicator?: boolean;

  /**
   * Places the label either above (default) or on the side of the control.
   * @default 'above'
   */
  labelPlacement?: 'side' | 'above';
}

export const RadioGroup = React.forwardRef(function RadioGroup(
  props: RadioGroupProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const {
    label: labelText,
    children,
    orientation = 'vertical',
    error,
    note,
    contextualHelp,
    onBlur,
    size = 'lg',
    necessityIndicator = false,
    labelPlacement = 'above',
    isDisabled,
    ...otherProps
  } = props;

  const state = useRadioGroupState(props);
  const { radioGroupProps, labelProps, errorMessageProps, descriptionProps } =
    useRadioGroup({ ...props, onBlur, isInvalid: !!error }, state);

  return (
    <Field
      {...otherProps}
      size={size}
      labelPlacement={labelPlacement}
      labelVerticalAlignment="top"
      noDefaultWidth
    >
      <Field.Context
        descriptionProps={error ? errorMessageProps : descriptionProps}
      >
        {(labelText || contextualHelp) && (
          <Field.Label>
            <Label
              {...labelProps}
              size={size}
              necessityIndicator={
                state.isRequired && necessityIndicator
                  ? 'required'
                  : !state.isRequired && necessityIndicator
                  ? 'optional'
                  : undefined
              }
              contextualHelp={contextualHelp}
            >
              {labelText}
            </Label>
          </Field.Label>
        )}
        <Field.Control>
          <div
            className={clsx(
              styles['sapphire-field-group'],
              styles[`sapphire-field-group--${orientation}` as const],
              {
                [styles['sapphire-field-group--md']]: size === 'md',
              }
            )}
            {...radioGroupProps}
            ref={ref}
          >
            <RadioGroupContext.Provider value={{ state, size }}>
              {children}
            </RadioGroupContext.Provider>
          </div>
        </Field.Control>
        {(error || note) && (
          <Field.Footer>
            {error ? (
              <Field.Note variant="error">{error}</Field.Note>
            ) : note ? (
              note
            ) : (
              <></>
            )}
          </Field.Footer>
        )}
      </Field.Context>
    </Field>
  );
});
